"use client"

import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay } from "date-fns"
import { cn } from "@/lib/utils"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import type { CalendarEvent } from "./calendar"
import UserSvg from "@/assets/userSvg.svg"
import { CalendarEventActions } from "./calendar-event-actions"

interface MonthViewProps {
    currentDate: Date
    events: CalendarEvent[]
    dateRange: { start: Date; end: Date }
    onViewEvent?: (event: CalendarEvent) => void
    onEditEvent?: (event: CalendarEvent) => void
    onDeleteEvent?: (event: CalendarEvent) => void
    refetch?: () => void
    access?: string
    hideEventActions?: boolean
}

export function MonthView({ currentDate, events, dateRange, onViewEvent, onEditEvent, onDeleteEvent, refetch, access, hideEventActions }: MonthViewProps) {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(monthStart)
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 })
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 })

    const rows = []
    let days = []
    let day = startDate
    const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]

    // Get events for a specific day
    const getEventsForDay = (day: Date) => {
        return events.filter((event) => isSameDay(new Date(event.start), day))
    }

    // Create calendar rows
    while (day <= endDate) {
        for (let i = 0; i < 7; i++) {
            const currentDay = day
            const dayEvents = getEventsForDay(currentDay)
            const isCurrentMonth = isSameMonth(day, monthStart)

            days.push(
                <div
                    key={day.toString()}
                    className={cn(
                        "border-r border-b min-h-[100px] p-1",
                        !isCurrentMonth && "bg-gray-50/30",
                        i === 3 && "bg-green-50/30", // Highlight Wednesday like in the screenshot
                        "overflow-y-auto",
                    )}
                >
                    <div className="flex flex-col h-full">
                        <div className={cn("text-sm mb-1", !isCurrentMonth && "text-gray-400")}>
                            {format(day, "d")}
                            {day.getDate() === 1 && <span className="text-xs text-gray-500 ml-1">{format(day, "MMM")}</span>}
                        </div>
                        <div className="flex-1 space-y-1">
                            {dayEvents.map((event) => {
                                // Map color to static Tailwind classes
                                const getColorClasses = (color?: string) => {
                                    const colorMap: Record<string, string> = {
                                        purple: "bg-purple-50 border-purple-200 hover:bg-purple-100",
                                        blue: "bg-blue-50 border-blue-200 hover:bg-blue-100",
                                        green: "bg-green-50 border-green-200 hover:bg-green-100",
                                        red: "bg-red-50 border-red-200 hover:bg-red-100",
                                        yellow: "bg-yellow-50 border-yellow-200 hover:bg-yellow-100",
                                        orange: "bg-orange-50 border-orange-200 hover:bg-orange-100",
                                        teal: "bg-teal-50 border-teal-200 hover:bg-teal-100",
                                        pink: "bg-pink-50 border-pink-200 hover:bg-pink-100",
                                        indigo: "bg-indigo-50 border-indigo-200 hover:bg-indigo-100",
                                        gray: "bg-gray-50 border-gray-200 hover:bg-gray-100",
                                    };
                                    return colorMap[color || ""] || "bg-blue-50 border-blue-200 hover:bg-blue-100";
                                };

                                return (
                                    <div
                                        key={event.id}
                                        className={cn(
                                            "text-xs p-1 rounded-md border transition-colors",
                                            getColorClasses(event.color),
                                        )}
                                    >
                                        <div className="flex items-start gap-1 mb-1">
                                            {event.user?.avatar && (
                                                <Avatar className="h-4 w-4">
                                                    <AvatarImage
                                                        src={`${import.meta.env.VITE_API_URL}/uploads/${event.user.avatar}`}
                                                        alt={event.user.name || "User"}
                                                    />
                                                    <AvatarFallback>
                                                        <img src={UserSvg} alt="User" className="h-4 w-4 rounded-full" />
                                                    </AvatarFallback>
                                                </Avatar>
                                            )}
                                            <div
                                                className="font-medium flex-1 cursor-pointer break-words leading-tight text-[11px] line-clamp-2"
                                                onClick={() => onViewEvent?.(event)}
                                                title={event.title}
                                            >
                                                {event.title}
                                            </div>
                                            {!hideEventActions && (
                                                <CalendarEventActions
                                                    event={event}
                                                    onView={onViewEvent}
                                                    onEdit={onEditEvent}
                                                    onDelete={onDeleteEvent}
                                                    refetch={refetch}
                                                    access={access}
                                                    size="sm"
                                                    variant="dropdown"
                                                />
                                            )}
                                        </div>
                                        {event.tags && (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {event.tags.map((tag, idx) => (
                                                    <span
                                                        key={idx}
                                                        className={cn("px-1.5 py-0.5 rounded-sm text-white text-[10px]", `bg-${tag.color}-500`)}
                                                    >
                                                        {tag.label}
                                                    </span>
                                                ))}
                                                {event.is_exclusive && (
                                                    <span
                                                        className={cn("px-1.5 py-0.5 rounded-sm text-white text-[10px]", `bg-red-500 text-white`)}
                                                    >
                                                        Exclusive
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>,
            )
            day = addDays(day, 1)
        }
        rows.push(
            <div key={day.toString()} className="grid grid-cols-7">
                {days}
            </div>,
        )
        days = []
    }


    return (
        <div className="absolute inset-0 flex flex-col overflow-hidden">
            <div className="sticky top-0 z-10 grid grid-cols-7 border-b bg-white shrink-0">
                {daysOfWeek.map((day, index) => (
                    <div
                        key={day}
                        className={cn(
                            "py-2 text-center text-[13px] border-r",
                            index === 3 && "bg-green-50/30", // Highlight Wednesday header
                        )}
                    >
                        {day}
                    </div>
                ))}
            </div>
            <div className="flex-1 overflow-y-auto">{rows}</div>
        </div>
    )
}

